import { encryptAndUploadBlob, type BlobUploadProgress } from '../api/blobUpload';
import { api } from '../api/client';
import {
  decryptFileFromChunks,
  splitPackedChunks,
} from './chunkedFile';
import type { InfoShareManifestPlaintext } from './infoShareFragment';
import {
  decodeBase64,
  decodeBase64Url,
  encodeBase64,
  encryptAesGcm,
  importAesKey,
  packEncryptedBlob,
  sha256,
  decryptAesGcm,
  unpackEncryptedBlob,
} from './primitives';
import { triggerBrowserDownload } from './sharedFolderFile';

const ATTACHMENT_NAME_AAD = 'shareweb:info-attachment-name:v1';

export type InfoShareAttachment = InfoShareManifestPlaintext['attachments'][number];

async function encryptAttachmentName(contentKey: Uint8Array, name: string): Promise<string> {
  const aesKey = await importAesKey(contentKey);
  const encrypted = await encryptAesGcm(
    aesKey,
    new TextEncoder().encode(name),
    new TextEncoder().encode(ATTACHMENT_NAME_AAD),
  );
  return encodeBase64(packEncryptedBlob(encrypted));
}

export async function encryptInfoShareAttachmentFile(
  contentKey: Uint8Array,
  file: File,
  onProgress?: (progress: BlobUploadProgress) => void,
): Promise<{
  attachment: InfoShareAttachment;
  ciphertextBytes: number;
}> {
  const fileName = file.name.trim();
  if (!fileName) {
    throw new Error('empty_file_name');
  }

  const fileId = crypto.randomUUID();
  const { blobId, manifest } = await encryptAndUploadBlob(file, {
    fileId,
    contentKey,
    onProgress,
  });
  const chunkCiphertextSizes = manifest.chunks.map((chunk) => chunk.ciphertextSizeBytes);

  return {
    attachment: {
      fileId,
      blobId,
      encryptedName: await encryptAttachmentName(contentKey, fileName),
      plaintextSha256: manifest.plaintextSha256,
      size: manifest.totalPlaintextBytes,
      chunkPlaintextBytes: manifest.chunkPlaintextBytes,
      chunkCiphertextSizes: manifest.chunks.length > 1 ? chunkCiphertextSizes : undefined,
    },
    ciphertextBytes: chunkCiphertextSizes.reduce((sum, size) => sum + size, 0),
  };
}

export async function encryptInfoShareAttachments(
  contentKey: Uint8Array,
  files: File[],
  onProgress?: (progress: BlobUploadProgress) => void,
): Promise<{
  attachments: InfoShareAttachment[];
  attachmentBlobIds: string[];
  attachmentBytes: number;
}> {
  const attachments: InfoShareAttachment[] = [];
  const attachmentBlobIds: string[] = [];
  let attachmentBytes = 0;
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  let completedBytes = 0;

  for (const file of files) {
    const encrypted = await encryptInfoShareAttachmentFile(contentKey, file, (progress) => {
      onProgress?.({
        ...progress,
        bytesCompleted: completedBytes + progress.bytesCompleted,
        bytesTotal: totalBytes,
      });
    });
    attachments.push(encrypted.attachment);
    attachmentBlobIds.push(encrypted.attachment.blobId);
    attachmentBytes += encrypted.ciphertextBytes;
    completedBytes += file.size;
  }

  return { attachments, attachmentBlobIds, attachmentBytes };
}

export async function decryptInfoShareAttachmentName(
  contentKey: Uint8Array,
  encryptedName: string,
): Promise<string> {
  const aesKey = await importAesKey(contentKey);
  const plaintext = await decryptAesGcm(
    aesKey,
    unpackEncryptedBlob(decodeBase64(encryptedName)),
    new TextEncoder().encode(ATTACHMENT_NAME_AAD),
  );
  return new TextDecoder().decode(plaintext);
}

export async function decryptInfoShareAttachmentFile(
  contentKey: Uint8Array,
  attachment: InfoShareAttachment,
): Promise<Uint8Array> {
  const blob = await api.fetchBlob(attachment.blobId);
  if (attachment.chunkPlaintextBytes != null) {
    const sizes = attachment.chunkCiphertextSizes ?? [blob.length];
    const encryptedChunks = splitPackedChunks(blob, sizes);
    return decryptFileFromChunks(
      {
        fileId: attachment.fileId,
        contentKey,
        plaintextSha256: attachment.plaintextSha256,
        totalPlaintextBytes: attachment.size,
        chunkPlaintextBytes: attachment.chunkPlaintextBytes,
        chunks: sizes.map((ciphertextSizeBytes, chunkIndex) => ({
          chunkIndex,
          ciphertextSizeBytes,
        })),
      },
      encryptedChunks,
    );
  }

  const aesKey = await importAesKey(contentKey);
  const plaintext = await decryptAesGcm(aesKey, unpackEncryptedBlob(blob));
  const digest = await sha256(plaintext);
  const expected = decodeBase64Url(attachment.plaintextSha256);
  if (digest.length !== expected.length || !digest.every((byte, index) => byte === expected[index])) {
    throw new Error('attachment_hash_mismatch');
  }

  return plaintext;
}

export async function downloadInfoShareAttachment(
  contentKey: Uint8Array,
  attachment: InfoShareAttachment,
): Promise<void> {
  const [name, bytes] = await Promise.all([
    decryptInfoShareAttachmentName(contentKey, attachment.encryptedName),
    decryptInfoShareAttachmentFile(contentKey, attachment),
  ]);
  triggerBrowserDownload(name, bytes);
}
